import * as React from "react"

import { cn } from "@/lib/utils"
import { formatNumber } from "@/lib/format"

/*
  Segmented — one of N views, each with an optional count (zero counts are muted) and tone dot.

    <Segmented ariaLabel="Which orders" value={view} onChange={setView}
      options={[{ key: 'open', label: 'Open', count: 3 }, { key: 'all', label: 'All', count: 12 }]} />

  options: [{ key, label, count?, dot? (status tone), title?, testId? }]; size 'md' (30px) | 'sm' (26px);
  fullWidth: the control fills its container with equal-width, centred segments (a pane's status control,
  a phone view switch).
*/
function Segmented({ options, value, onChange, ariaLabel, className, size = "md", fullWidth = false, testId }) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      data-testid={testId}
      className={cn(
        "max-w-full shrink-0 gap-0.5 overflow-x-auto rounded-[11px] bg-[color:var(--status-gray-bg)] p-[3px]",
        fullWidth ? "grid w-full auto-cols-fr grid-flow-col" : "inline-flex",
        className
      )}
    >
      {options.map((option) => {
        const active = option.key === value
        const zero = option.count === 0
        return (
          <button
            key={option.key}
            type="button"
            aria-pressed={active}
            data-testid={option.testId}
            data-state={active ? "on" : "off"}
            title={option.title}
            onClick={() => onChange(option.key)}
            className={cn(
              "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              fullWidth && "min-w-0 justify-center",
              size === "sm" ? "h-[26px] px-2 text-[12.5px]" : "h-[30px] px-2.5 text-[13px]",
              active ? "bg-surface text-ink shadow-card" : zero ? "text-ink-3 hover:text-ink-2" : "text-ink-2 hover:text-ink"
            )}
          >
            {option.dot ? <span className={cn("ui-tone-dot", `tone-${option.dot}`)} aria-hidden="true" /> : null}
            <span>{option.label}</span>
            {option.count !== undefined && option.count !== null ? (
              <span className={cn("text-[11.5px] font-semibold tabular-nums", zero && !active ? "text-ink-4" : "text-ink-3")}>
                {formatNumber(option.count, { maxDecimals: 0 })}
              </span>
            ) : null}
          </button>
        )
      })}
    </div>
  )
}

export { Segmented }
